import { Component } from "@theme/component";

export default class ProteinCalculatorComponent extends Component {
  requiredRefs = [
    "sliderTrack",
    "sliderProgress",
    "sliderThumb",
    "tooltipValue",
    "weightInput",
    "tooltip",
  ];

  weightMin = 40;
  weightMax = 300;
  weightCurrent = 140;
  weightUnit = "lbs";
  maintenanceMultiplier = 0.36;
  weightLossMultiplier = 0.7;
  muscleGainMultiplier = 0.8;

  isDragging = false;

  connectedCallback() {
    super.connectedCallback();

    this.weightMin = parseFloat(this.dataset.weightMin ?? "40");
    this.weightMax = parseFloat(this.dataset.weightMax ?? "300");
    this.weightCurrent = parseFloat(this.dataset.weightDefault ?? "140");
    this.weightUnit = this.dataset.weightUnit ?? "lbs";
    this.maintenanceMultiplier = parseFloat(
      this.dataset.maintenanceMultiplier ?? "0.36"
    );
    this.weightLossMultiplier = parseFloat(
      this.dataset.weightLossMultiplier ?? "0.7"
    );
    this.muscleGainMultiplier = parseFloat(
      this.dataset.muscleGainMultiplier ?? "0.8"
    );

    this.boundHandlePointerMove = this.handlePointerMove.bind(this);
    this.boundHandlePointerUp = this.handlePointerUp.bind(this);
    this.boundHandleTrackPointerDown = this.handleTrackPointerDown.bind(this);
    this.boundHandleThumbPointerDown = this.handleThumbPointerDown.bind(this);

    const track = this.refs.sliderTrack;
    const thumb = this.refs.sliderThumb;

    if (track) {
      track.addEventListener("pointerdown", this.boundHandleTrackPointerDown);
    }
    if (thumb) {
      thumb.addEventListener("pointerdown", this.boundHandleThumbPointerDown);
    }

    document.addEventListener("pointermove", this.boundHandlePointerMove);
    document.addEventListener("pointerup", this.boundHandlePointerUp);
    document.addEventListener("pointercancel", this.boundHandlePointerUp);

    this.updateUI();
    this.calculateResults();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const track = this.refs.sliderTrack;
    const thumb = this.refs.sliderThumb;

    if (track) {
      track.removeEventListener(
        "pointerdown",
        this.boundHandleTrackPointerDown
      );
    }
    if (thumb) {
      thumb.removeEventListener(
        "pointerdown",
        this.boundHandleThumbPointerDown
      );
    }

    document.removeEventListener("pointermove", this.boundHandlePointerMove);
    document.removeEventListener("pointerup", this.boundHandlePointerUp);
    document.removeEventListener("pointercancel", this.boundHandlePointerUp);
  }

  handleInputChange(event) {
    const input = event.target;
    let value = parseFloat(input.value);

    if (isNaN(value)) {
      value = this.weightMin;
    }

    value = Math.max(this.weightMin, Math.min(this.weightMax, value));
    this.weightCurrent = value;

    this.updateUI();
    this.calculateResults();
  }

  handleTrackPointerDown(event) {
    event.preventDefault();

    const track = this.refs.sliderTrack;
    if (track) {
      track.setPointerCapture(event.pointerId);
    }

    this.isDragging = true;
    this.setDraggingStyles(true);
    this.updateSliderFromEvent(event);
  }

  handleThumbPointerDown(event) {
    event.preventDefault();
    event.stopPropagation();

    const track = this.refs.sliderTrack;
    if (track) {
      track.setPointerCapture(event.pointerId);
    }

    this.isDragging = true;
    this.setDraggingStyles(true);
  }

  handlePointerMove(event) {
    if (!this.isDragging) return;
    event.preventDefault();
    this.updateSliderFromEvent(event);
  }

  handlePointerUp(event) {
    if (!this.isDragging) return;
    this.isDragging = false;

    const track = this.refs.sliderTrack;
    if (track && event.pointerId !== undefined) {
      try {
        track.releasePointerCapture(event.pointerId);
      } catch (e) {}
    }

    this.setDraggingStyles(false);
  }

  setDraggingStyles(dragging) {
    const thumb = this.refs.sliderThumb;
    const progress = this.refs.sliderProgress;
    const tooltip = this.refs.tooltip;

    if (dragging) {
      if (thumb) {
        thumb.style.cursor = "grabbing";
        thumb.style.transition = "none";
      }
      if (progress) progress.style.transition = "none";
      if (tooltip) tooltip.style.transition = "none";
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      if (thumb) {
        thumb.style.cursor = "";
        thumb.style.transition = "";
      }
      if (progress) progress.style.transition = "";
      if (tooltip) tooltip.style.transition = "";
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
  }

  updateSliderFromEvent(event) {
    const track = this.refs.sliderTrack;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));

    const value =
      this.weightMin + (this.weightMax - this.weightMin) * percentage;
    this.weightCurrent = Math.round(value);

    this.updateUI();
    this.calculateResults();
  }

  updateUI() {
    const percentage =
      ((this.weightCurrent - this.weightMin) /
        (this.weightMax - this.weightMin)) *
      100;

    const progress = this.refs.sliderProgress;
    if (progress) {
      progress.style.width = `${percentage}%`;
    }

    const thumb = this.refs.sliderThumb;
    if (thumb) {
      thumb.style.left = `${percentage}%`;
      thumb.setAttribute("aria-valuenow", this.weightCurrent.toString());
    }

    const tooltipValue = this.refs.tooltipValue;
    if (tooltipValue) {
      tooltipValue.textContent = `${this.weightCurrent} ${this.weightUnit}`;
    }

    const tooltip = this.refs.tooltip;
    if (tooltip) {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (isMobile) {
        const track = this.refs.sliderTrack;
        if (track) {
          const trackWidth = track.offsetWidth;
          const tooltipWidth = tooltip.offsetWidth;
          const halfTooltip = tooltipWidth / 2;
          const rawLeft = (percentage / 100) * trackWidth;
          const clampedLeft = Math.max(
            halfTooltip,
            Math.min(rawLeft, trackWidth - halfTooltip)
          );
          tooltip.style.left = `${clampedLeft}px`;
        } else {
          tooltip.style.left = `${percentage}%`;
        }
      } else {
        tooltip.style.left = `${percentage}%`;
      }
    }

    const weightInput = this.refs.weightInput;
    if (weightInput && document.activeElement !== weightInput) {
      weightInput.value = this.weightCurrent.toString();
    }
  }

  calculateResults() {
    const maintenance = (
      this.weightCurrent * this.maintenanceMultiplier
    ).toFixed(1);
    const weightLoss = (this.weightCurrent * this.weightLossMultiplier).toFixed(
      1
    );
    const muscleGain = (this.weightCurrent * this.muscleGainMultiplier).toFixed(
      1
    );

    const maintenanceValue = this.refs.maintenanceValue;
    if (maintenanceValue) {
      maintenanceValue.textContent = `${maintenance}g`;
    }

    const weightLossValue = this.refs.weightLossValue;
    if (weightLossValue) {
      weightLossValue.textContent = `${weightLoss}g`;
    }

    const muscleGainValue = this.refs.muscleGainValue;
    if (muscleGainValue) {
      muscleGainValue.textContent = `${muscleGain}g`;
    }
  }
}

if (!customElements.get("protein-calculator-component")) {
  customElements.define(
    "protein-calculator-component",
    ProteinCalculatorComponent
  );
}
